import React, { useEffect, useRef, useState } from 'react';
import AppHeader from '../../common/app-header/AppHeader';
import { useAppDispatch } from '../../common/hooks/useAppDispatch';
import { Box, CircularProgress, Grid, Stack, Typography } from '@mui/material';

import { useTranslation } from 'react-i18next';
import BasicTabs from '../../common/basic-tabs/BasicTabs';
import DetailsTabs from './components/DetailsTab';
import DocumentsTabs from './components/DocumentsTabs';
import NotesTab from '../../common/notes-tab/NotesTab';
import FormUpload from '../../common/form-upload/FormUpload';
import UploadButton from '../../common/upload-button/UploadButton';
import { useAppSelector } from '../../common/hooks/useAppSelector';

import styles from './EmployeeProfile.module.scss';
import CreateContactModal from '../../common/create-contact-modal/CreateContactModal';
import { selectAllCompanies } from '../../slices/companies/companiesSlice';
import { fetchEmployees, selectEmployeeById } from '../../slices/employees/employeesSlice';
import { RootState } from '../../store';
import { Link, useParams } from 'react-router-dom';
import { ROUTES } from '../../common/constants/routes';
import { useDocuments } from '../document-vault/hooks/useDocuments';
import InfoIcon from '@mui/icons-material/Info';
import PreviewTooltip from '../../common/preview-block/PreviewTooltip';
import Preview from './components/preview-block/Preview';
import EditNoteOutlinedIcon from '@mui/icons-material/EditNoteOutlined';
import { useDocumentSigning } from '../document-vault/hooks/useDocumentSigning';
import { showOverlay } from '../../slices/show-progress-overlay/showProgressOverlay';
import { DocumentVaultItemModel } from '../../models/document.vault.item.model';
import { useAssignProfilePersonModal } from '../../common/assign-profile-person-modal/useAssignProfilePersonModal';
import { AssignProfilePersonModal } from '../../common/assign-profile-person-modal/AssignProfilePersonModal';
import { Document } from '../document-vault/components/document-list-vault/types';
import { selectUserRoleByGroupName } from '../../slices/user/userSlice';
import { GROUPS } from '../../common/constants/groups';
import { useProfileDocumentsQuery } from '../document-vault/hooks/useProfileDocumentsQuery';
import { DOCUMENTS_PER_PAGE } from '../document-vault/hooks/vaultQueryParams';

const EmployeeProfile = () => {
  const dispatch = useAppDispatch();
  const [isUploadOpen, setIsUploadOpen] = useState(false);
  const [isUploadAndSignOpen, setIsUploadAndSignOpen] = useState(false);
  // Profile tab, selected workflow, card/list view and page live in the URL query,
  // so the browser Back button from a document details page restores the same view.
  const {
    tab: activeTab,
    setTab: setActiveTab,
    workflow,
    setWorkflow,
  } = useProfileDocumentsQuery();
  const company = useAppSelector(selectAllCompanies)[0];
  const documentsLoading = useAppSelector((state: RootState) => state.documents.status);
  const userIsEmployee = Boolean(
    useAppSelector(selectUserRoleByGroupName(GROUPS.COMPANY_EMPLOYEE))
  );
  const formUploadRef = useRef<HTMLDivElement>(null);
  const uploadButtonRef = useRef<HTMLButtonElement>(null);
  const { employeeId } = useParams();
  const parsedEmployeeId = Number(employeeId);
  const sortByDate = (a: any, b: any) => {
    return Number(new Date(b.created)) - Number(new Date(a.created));
  };
  const employee = useAppSelector((state: RootState) =>
    selectEmployeeById(state, parsedEmployeeId)
  );

  const {
    documents,
    documentsCount,
    documentsPage,
    getRequestedPage,
    showWorkflowBreadcrumbs,
    setShowWorkflowBreadcrumbs,
    cardView,
    setCardView,
  } = useDocuments('employment', workflow ?? undefined, parsedEmployeeId);
  const combinedDocuments = [...documents].slice().sort((a, b) => sortByDate(a, b));

  // Items per page - adjust this value according to your API
  // Calculate total pages
  const totalPages = Math.ceil(documentsCount / DOCUMENTS_PER_PAGE);

  // Handle page change
  const handlePageChange = (event: React.ChangeEvent<unknown>, page: number) => {
    // Written to the URL (?page=N); the documents are fetched from it
    getRequestedPage(page);
  };

  useEffect(() => {
    if (company?.id) {
      dispatch(fetchEmployees({ company_id: company.id }));
    }
  }, [company?.id, dispatch]);

  const { t } = useTranslation();
  const [isModalOpen, setIsModalOpen] = useState(false);

  const handleUploadClose = () => {
    setIsUploadOpen(false);
  };

  // Actually handles both open & close
  const handleUploadOpen = () => {
    setIsUploadOpen((prev) => !prev);
  };

  /*useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const targetElement = event.target as HTMLElement;
      if (
        targetElement.closest('.MuiMenuItem-root') || // Ignore clicks on MenuItem
        targetElement.closest('.MuiSelect-root') // Ignore clicks on Select
      ) {
        return;
      }
      if (
        formUploadRef.current &&
        !formUploadRef.current.contains(event.target as Node) &&
        uploadButtonRef.current &&
        !uploadButtonRef.current.contains(event.target as Node)
      ) {
        setIsUploadOpen(false);
      }
    };

    if (isUploadOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    } else {
      document.removeEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isUploadOpen]);*/

  const toogleShowAddlModal = () => {
    setIsModalOpen((prev) => !prev);
  };

  const {
    document: documentToSign,
    setDocument: setDocumentToSign,
    handleSignOriginalDocument,
    handleSignLatestVersion,
    setOpenAssignPersonDialog,
    openAssignPersonDialog,
  } = useDocumentSigning(cardView);
  const {
    patchDocumentWithProfilePerson,
    setAssignee,
    assignee /*checkProfilePersonsAvailability */,
  } = useAssignProfilePersonModal(documentToSign?.category, documentToSign);

  const handleUploadAndSignClose = () => {
    setIsUploadAndSignOpen(false);
  };

  const handleUploadAndSignOpen = () => {
    setIsUploadAndSignOpen(true);
  };

  const handleUploadAndSignSuccess = (uploadedDocument: DocumentVaultItemModel, tab?: Window) => {
    const document = uploadedDocument as Document;
    /*if (
      (document.customers?.length ?? 0) === 0 &&
      (document.employees?.length ?? 0) === 0 &&
      (document.suppliers?.length ?? 0) === 0 &&
      (document.business_profile_contacts?.length ?? 0) === 0 &&
      checkProfilePersonsAvailability(document.category ?? '')
    ) {
      dispatch(hideOverlay());
      if (tab) {
        tab.close();
      }
      setDocumentToSign(document);
      setOpenAssignPersonDialog(true);
    } else {*/
    handleSignOriginalDocument(document, undefined, undefined, tab);
    //}
  };

  if (!company || !employee) {
    return (
      <>
        <AppHeader title={t('employeeProfiles.header')} />
        <Box
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            height: 'calc(100vh - 104px)',
            flexWrap: 'wrap',
            rowGap: '8px',
          }}
        >
          <CircularProgress color='primary' size={50} />
        </Box>
      </>
    );
  }

  return (
    <>
      <AppHeader title={t('employeeProfiles.header')} />
      <Grid component='div' container spacing={3} sx={{ marginTop: '16px' }}>
        <Grid component='div' size={{ xs: 12 }}>
          <Box className={styles.breadcrumbs}>
            <Link
              id='employeeProfileHeaderLink'
              className={styles.documentViewText}
              to={ROUTES.EMPLOYEE_PROFILES}
            >
              {t('employeeProfiles.header')}
            </Link>
            <Typography variant='body2' className={styles.separator}>
              ›
            </Typography>
            <Typography variant='body2' className={styles.documentViewText}>
              {employee.full_name}
            </Typography>
          </Box>
          <Stack component='div' spacing={2}>
            <Typography variant='h1' component='h1' className={styles.page_title}>
              {employee.full_name}
            </Typography>
            <Stack
              component='div'
              direction='row'
              spacing={1}
              useFlexGap
              sx={{
                alignItems: 'center',
                flexWrap: 'wrap',
                display: { xs: 'block', md: 'flex' },
                justifyContent: 'space-between',
                marginTop: '8px',
              }}
            >
              <Stack
                sx={{
                  alignItems: 'center',
                  flexDirection: 'row',
                  flexWrap: 'wrap',
                  gap: '8px',
                }}
              >
                <Box ref={uploadButtonRef} style={{ position: 'relative' }}>
                  <UploadButton
                    id='employeeProfileUploadFilesBtn'
                    onClick={handleUploadOpen}
                    buttonText={t('buttons.uploadFiles')}
                    isArrowDown={false}
                  />
                  {isUploadOpen && (
                    <Box
                      className={styles.form_box}
                      ref={formUploadRef}
                      style={{ alignSelf: 'flex-start' }}
                    >
                      <FormUpload
                        onCancel={handleUploadClose}
                        categoryValue='employment'
                        employees={[employee]}
                        workflow={workflow}
                      />
                    </Box>
                  )}
                </Box>

                {!userIsEmployee && (
                  <Box style={{ position: 'relative' }}>
                    <UploadButton
                      id='employeeProfileUploadAndSignPdfBtn'
                      onClick={handleUploadAndSignOpen}
                      buttonText={t('buttons.uploadAndSignPDF')}
                      isArrowDown={false}
                      icon={EditNoteOutlinedIcon}
                    />
                    {isUploadAndSignOpen && (
                      <Box className={styles.form_box} style={{ alignSelf: 'flex-start' }}>
                        <FormUpload
                          onCancel={handleUploadAndSignClose}
                          categoryValue='employment'
                          employees={[employee]}
                          workflow={workflow}
                          uploadWithSigning={true}
                          accept='.pdf'
                          supportedFormatsKey='messages.supportedFormatsPDF'
                          maxFiles={1}
                          onUploadSuccess={handleUploadAndSignSuccess}
                        />
                      </Box>
                    )}
                  </Box>
                )}

                <PreviewTooltip title={<Preview />} arrow placement='top'>
                  <InfoIcon
                    color='primary'
                    fontSize='small'
                    sx={{ display: { xs: 'none', md: 'inline-block' } }}
                  />
                </PreviewTooltip>
              </Stack>
            </Stack>
          </Stack>
        </Grid>
        <Grid component='div' size={{ xs: 12 }} className={styles.grid_margin_bottom}>
          <BasicTabs
            tabs={[
              /*t('businessProfile.allTab'),*/
              t('businessProfile.documentsTabs'),
              t('businessProfile.detailsTab'),
              t('businessProfile.notesTab'),
            ]}
            activeTab={activeTab}
            onTabChange={setActiveTab}
            tabContents={[
              /*<AllTabs
                                        employee={employee}
                                        company={company}
                                        documents={combinedDocuments}
                                        key='all'
                                      />,*/
              <DocumentsTabs
                documents={combinedDocuments}
                workflow={workflow}
                setWorkflow={setWorkflow}
                cardView={cardView}
                showWorkflowBreadcrumbs={showWorkflowBreadcrumbs}
                setShowWorkflowBreadcrumbs={setShowWorkflowBreadcrumbs}
                key='documents'
                documentsActions={{
                  idPrefix: 'employeeProfile',
                  cardView,
                  onCardView: () => setCardView(true),
                  onListView: () => setCardView(false),
                  listViewDisabled: !!workflow && showWorkflowBreadcrumbs,
                  showPagination: documents.length > 0 && (!!workflow || !cardView),
                  page: documentsPage,
                  totalPages,
                  onPageChange: handlePageChange,
                  paginationDisabled: documentsLoading === 'loading',
                }}
              />,
              <DetailsTabs employee={employee} company={company} key='details' />,
              <NotesTab employee={employee} key='notes' />,
            ]}
          />
        </Grid>
        {isModalOpen && (
          <CreateContactModal
            companyId={company.id as number}
            open={isModalOpen}
            onClose={() => toogleShowAddlModal()}
          />
        )}
        {openAssignPersonDialog && documentToSign && (
          <AssignProfilePersonModal
            open={openAssignPersonDialog}
            onClose={() => {
              setOpenAssignPersonDialog(false);
              setDocumentToSign(null as unknown as Document);
              setAssignee(null as unknown as string);
            }}
            modalTitle={t('headers.startSigning')}
            category={documentToSign?.category ?? ''}
            document={documentToSign}
            goBackButtonText={t('buttons.goBack')}
            modalMessage={t('messages.signingRecommendations')}
            assignee={assignee}
            setAssignee={setAssignee}
            signOriginalDocument={true}
            secondaryAction={() => {
              dispatch(showOverlay());
              const url = `${window.location.origin}${ROUTES.PROCESSING}`;
              const tab = window.open('', '_blank');
              // @ts-expect-error: there can be an error
              tab.location.href = url;
              if (assignee && assignee !== 'noAssignee') {
                patchDocumentWithProfilePerson()
                  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                  //@ts-ignore
                  .then((res) => handleSignOriginalDocument(res, undefined, undefined, tab))
                  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                  //@ts-ignore
                  .catch(() => handleSignOriginalDocument(documentToSign, undefined, true, tab));
              } else {
                // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                //@ts-ignore
                handleSignOriginalDocument(documentToSign, undefined, true, tab);
              }
            }}
            primaryAction={() => {
              dispatch(showOverlay());
              const url = `${window.location.origin}${ROUTES.PROCESSING}`;
              const tab = window.open('', '_blank');
              // @ts-expect-error: there can be an error
              tab.location.href = url;
              if (assignee && assignee !== 'noAssignee') {
                patchDocumentWithProfilePerson()
                  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                  //@ts-ignore
                  .then((res) => handleSignLatestVersion(res, undefined, undefined, undefined, tab))
                  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                  //@ts-ignore
                  .catch(() =>
                    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                    //@ts-ignore
                    handleSignLatestVersion(documentToSign, undefined, undefined, undefined, tab)
                  );
              } else {
                // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                //@ts-ignore
                handleSignLatestVersion(documentToSign, undefined, undefined, undefined, tab);
              }
            }}
          />
        )}
      </Grid>
    </>
  );
};

export default EmployeeProfile;
